// src/types.ts - ENHANCED: Support both string and number for billing fields
export interface Company {
  // Core identity fields
  UUID?: string;
  
  // Company information
  COMPANY_NAME?: string;
  COMPANY_DOMAIN?: string;
  COMPANY_DESCRIPTION?: string;
  COMPANY_INDUSTRY?: string;
  COMPANY_EMPLOYEE_COUNT?: string;
  COMPANY_NUM_EMPLOYEE_RANGE?: string;
  COMPANY_REVENUE_RANGE?: string;
  COMPANY_OWNERSHIP?: string;
  COMPANY_LATEST_FUNDING_STAGE?: string;
  COMPANY_TOTAL_FUNDING?: string;
  COMPANY_STOCK_EXCHANGE?: string;
  COMPANY_TICKER?: string;
  COMPANY_STOCK_SYMBOL?: string;
  COMPANY_FOUNDED_YEAR?: string;
  
  // Location information
  COMPANY_CITY?: string;
  COMPANY_STATE?: string;
  COMPANY_COUNTRY?: string;
  COMPANY_STATE_CODE?: string;
  COMPANY_COUNTRY_ALPHA2?: string;
  COMPANY_COUNTRY_ALPHA3?: string;
  
  // Social profiles
  COMPANY_LINKEDIN_URL?: string;
  COMPANY_FACEBOOK_URL?: string;
  COMPANY_TWITTER_URL?: string;
  COMPANY_ANGELLIST_URL?: string;
  
  // Technologies
  COMPANY_TECHNOLOGIES?: string;
  
  // Logo
  COMPANY_LOGO_URL?: string;

  // Allow dynamic property access (matches Contact interface)
  [key: string]: unknown;
}

// Union type for table data
export type TableData = Contact | Company;

export interface Contact {
  // Core identity fields
  UUID?: string;
  FIRST_NAME?: string;
  LAST_NAME?: string;
  MIDDLE_NAME?: string;
  
  // Contact information
  CURRENT_BUSINESS_EMAIL?: string;
  CURRENT_BUSINESS_EMAIL_VALIDATION_STATUS?: string;
  EMAILS?: string;
  PHONES?: string;
  MOBILE_PHONE?: string;
  MOBILE_PHONE_VALIDATION_STATUS?: string;
  
  // Professional information
  JOB_TITLE?: string;
  DEPARTMENT?: string;
  SENIORITY_LEVEL?: string;
  HEADLINE?: string;
  SUMMARY?: string;
  SKILLS?: string[] | string;
  WORK_EXPERIENCE?: unknown;
  EDUCATION_HISTORY?: unknown;
  POSTS?: unknown;
  
  // Company information
  COMPANY_NAME?: string;
  COMPANY_DOMAIN?: string;
  COMPANY_DESCRIPTION?: string;
  COMPANY_INDUSTRY?: string;
  COMPANY_EMPLOYEE_COUNT?: string;
  COMPANY_REVENUE_RANGE?: string;
  COMPANY_OWNERSHIP?: string;
  COMPANY_LATEST_FUNDING_STAGE?: string;
  COMPANY_STOCK_EXCHANGE?: string;
  
  // Location information
  PERSONAL_CITY?: string;
  PERSONAL_STATE?: string;
  PERSONAL_COUNTRY?: string;
  COMPANY_CITY?: string;
  COMPANY_STATE?: string;
  COMPANY_COUNTRY?: string;
  COMPANY_STATE_CODE?: string;
  COMPANY_COUNTRY_ALPHA2?: string;
  COMPANY_COUNTRY_ALPHA3?: string;
  
  // Social profiles
  LINKEDIN_URL?: string;

  // Technical fields
  LAST_UPDATED?: string;
  
  // UI image fields
  PHOTO_URL?: string;
  COMPANY_LOGO_URL?: string;
  
  // Contacts-specific fields
  owner_id?: string;
  saved_at?: string;
  person_uuid?: string;
  saved_source?: string;
  saved_version?: string;
  list_ids?: string[];
  
  [key: string]: unknown;
}

export interface RateLimitInfo {
  remaining: number;
  reset: number;
  limit: number;
}

export interface CircuitBreakerState {
  state: 'closed' | 'open' | 'half-open';
  failureCount: number;
  canRequest: boolean;
}

export interface User {
  api_key: string;
  name: string;
  email?: string;
  permissions: string[];
  is_active: boolean;
  rate_limit?: string | null;
  search_preview_limited?: boolean;
  created_at: string;
  updated_at: string;
  // ENHANCED: Billing fields that can be either string or number from API
  balance?: string | number;
  stripe_customer_id?: string;
  stripe_payment_method_id?: string;
  billing_email?: string;
  billing_enabled?: boolean;
  total_contacts_unlocked?: string | number;
  total_spent?: string | number;
  last_charge_date?: string | null;
  last_charge_amount?: string | number | null;
  pending_charge_amount?: string | number;
  custom_price_per_contact?: string | number | null;
}

export interface LogEntry {
  request_id: string;
  timestamp: string;
  user_id_hash: string | null;
  user_name?: string | null;
  error?: string | null;
  path: string;
  method: string;
  response_status: number;
  response_time_ms: number;
  ip_address: string;
  user_agent?: string;
  request_summary?: string | null;
  response_size_bytes?: number | null;
}

export interface LogFilters {
  startDate: string;
  endDate: string;
  userApiKey: string;
  path: string;
  method: string;
  statusCode: string;
  ipAddress: string;
  hasError: string;
}

export interface UserList {
  list_id: string;
  owner_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  contact_count: number;
  tags?: string[];
  is_default?: boolean;
}

export interface AccountList {
  list_id: string;
  owner_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  account_count: number;
  tags?: string[];
  is_default?: boolean;
}

export type FieldType = 'picklist' | 'keyword' | 'date_range' | 'range_slider' | 'text' | 'existence';
export type DataSourceType = 'people' | 'contacts' | 'company' | 'accounts';

export interface FieldDefinition {
  label: string;
  type: FieldType;
  priority: number;
  sortable: boolean;
  section: string;
  cardinality?: string;
  supports_operator?: boolean;
  supports_existence?: boolean;
  date_format?: string;
  category?: string;
  range_config?: { format?: string; is_text_field?: boolean; default_min?: number; default_max?: number; };
  no_commas?: boolean;
  contacts_only?: boolean;
  internal?: boolean;
}

export interface ColumnConfig {
  id: string;
  label: string;
  type: 'composite' | 'simple';
  sort_field: string;
  visible: boolean;
  visible_search?: boolean;
  always_visible?: boolean;
  render_component: string;
  width?: number;
  required_fields?: string[];
}

export interface DataSourceConfig {
  index: string;
  description: string;
  supports_selection: boolean;
  supports_actions: string[];
  sections: Record<string, string[]>;
}

export interface SchemaResponse {
  success: boolean;
  field_definitions: Record<string, FieldDefinition>;
  sections: Record<string, string[]>;
  global_search_fields: string[];
  column_configs: ColumnConfig[];
  ui_config: {
    max_results_per_page: number;
    default_page_size: number;
    max_filter_values: number;
    max_filters: number;
    max_dropdown_options: number;
    max_displayed_options: number;
    max_keywords_per_field: number;
    max_suggestions: number;
    categories: Record<string, unknown>;
    default_sorting: { field: string; order: string; };
    sidebar: { collapsed_width: number; expanded_width: number; icon_size: number; };
    picklist_ordering: Record<string, string[]>;
    billing?: { price_per_contact: string; };
    rate_limits?: { defaults: Record<string, string>; refresh_seconds: number; custom_scope: string; };
  };
  data_sources: Record<string, DataSourceConfig>;
}

export interface FieldStatistics {
  value_counts: Record<string, number>;
  total_documents: number;
  distinct_values: number;
  field_info: FieldDefinition;
  min_value?: number;
  max_value?: number;
  error?: string;
}

export interface MetadataResponse {
  field_statistics: Record<string, FieldStatistics>;
  total_matching_documents?: number;
  cache_status?: string;
  query_time_ms?: number;
  error?: string;
  data_source?: DataSourceType;
}

export interface SearchAccess {
  restricted: boolean;
  preview_limit: number;
  accessible_results: number;
  accessible_pages: number;
}

export interface SearchResponse {
  hits: Contact[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
  query_time_ms: number;
  data_source?: DataSourceType;
  search_access?: SearchAccess | null;
}

export interface KeywordFilter {
  type: 'keyword';
  values: string[];
  operator: 'OR' | 'AND';
  match_mode?: 'like' | 'exact';
}

export interface KeywordGroup {
  mode: 'AND' | 'OR' | 'EXCLUDE';
  keywords: string[];
  match_mode?: 'like' | 'exact';
}

export interface GroupedKeywordFilter {
  type: 'keyword_group';
  groups: KeywordGroup[];
}

export interface PicklistFilter {
  type: 'picklist';
  values: string[];
  operator: 'OR' | 'AND';
}

export interface DateRangeFilter {
  type: 'date_range';
  mode: 'before' | 'after' | 'between';
  start_date?: string;
  end_date?: string;
}

export interface RangeSliderFilter {
  type: 'range_slider';
  min_value?: number;
  max_value?: number;
}

export interface ExistenceFilter {
  type: 'existence';
  has_value: boolean;
}

export type FilterValue = string | string[] | boolean | null | KeywordFilter | PicklistFilter | DateRangeFilter | RangeSliderFilter | GroupedKeywordFilter | ExistenceFilter;
export type SortingState = Array<{ id: string; desc?: boolean }>;

export interface SearchState {
  query: string;
  page: number;
  sorting: SortingState;
  filters: Record<string, FilterValue>;
  dataSource: DataSourceType;
}

export interface SelectionState {
  selectedIds: Set<string>;
  selectAll: boolean;
  lastSelected: string | null;
}

export interface ProcessingState {
  uuids: Set<string>;
  operation: 'delete' | null;
  jobId: string | null;
}

export interface ConfirmationDialog {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant: 'danger' | 'warning' | 'info';
}

export interface ToastMessage {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string | ToastDetailMetrics;
  timestamp: number;
  duration?: number;
}

export interface ToastDetailMetrics {
  total: number;
  netNew?: number;
  refresh?: number;
  added?: number;
  updated?: number;
  cost?: number;
  label: string; // e.g., "contacts unlocked", "accounts unlocked"
}

export interface Job {
  job_id: string;
  owner_id: string;
  job_type: 'export' | 'add_to_list' | 'contacts_unlock' | 'delete_contacts' | 'remove_from_list' | 'enrichment_export' | 'accounts_unlock' | 'remove_from_account_list' | 'delete_accounts';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'canceled';
  progress: number; // 0-100
  params: Record<string, unknown>;
  result: JobResult | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface JobResult {
  // Core counts
  record_count?: number;
  total_count?: number; // total found by query
  processed_count?: number;
  
  // Unlock-specific counts (backend uses these exact field names)
  contacts_delivered?: number;  // Primary count for unlock jobs
  net_new_contacts?: number;    // New contacts that didn't exist before
  refresh_contacts?: number;    // Existing contacts that were refreshed with new data
  already_existed_count?: number;
  
  // Account unlock-specific counts
  accounts_added?: number;      // New accounts that were added
  accounts_updated?: number;    // Existing accounts that were updated
  accounts_skipped?: number;    // Accounts that were skipped (no changes needed)
  people_not_found_count?: number;
  requested_count?: number;
  
  // Backend JobResultState fields for progress tracking
  total_candidates?: number;
  scanned_candidates?: number;
  new_contacts_found?: number;
  existing_contacts_found?: number;
  contacts_updated?: number;
  contacts_charged?: number;
  contacts_failed?: number;
  update_errors?: number;
  batches_processed?: number;
  delivery_failures?: unknown[];
  
  // Other operation counts
  created_count?: number;
  added_count?: number;
  deleted_count?: number;
  removed_count?: number;
  failed_count?: number;
  already_in_list_count?: number;
  not_found_count?: number;
  
  // File operations
  download_url?: string;
  signed_download_url?: string;
  file_name?: string;
  file_size_bytes?: number;
  
  // System info
  message?: string;
  
  // Billing info - FLAT STRUCTURE
  total_cost?: string | number;
  
  // Nested billing structure for compatibility
  billing?: {
    job_cost?: string | number;
    amount_spent?: string | number;
    auto_charged?: boolean;
    charge_amount?: string | number;
  };
}

export interface NavigationState {
  view: 'people' | 'list' | 'company';
  listId: string | null;
}

export interface AppState {
  initialized: boolean;
  initializing: boolean;
  schema: SchemaResponse | null;
  schemaLoading: boolean;
  schemaError: string | null;
  metadata: MetadataResponse | null;
  metadataLoading: boolean;
  metadataError: string | null;
  keywordSuggestions: Record<string, string[]> | null;
  contacts: Contact[];
  selection: SelectionState;
  loading: boolean;
  error: string | null;
  apiConnected: boolean;
  currentView: 'main' | 'terms' | 'privacy' | 'activity' | 'admin' | 'billing' | 'enrichment';
  searchState: SearchState;
  totalResults: number;
  totalPages: number;
  searchAccess: SearchAccess | null;
  pageSize: number;
  lastSearchTime: number | null;
  visibleColumns: string[];
  toasts: ToastMessage[];
  confirmationDialog: ConfirmationDialog | null;
  activeJobs: Job[];
  navigationState: NavigationState;
  dataSourceCounts: {
    people: number | null;
    contacts: number | null;
    company: number | null;
  };
  processing: ProcessingState;
  billingBalance: BillingBalance | null;
  billingLoading: boolean;
  billingError: string | null;
  group_contact_keyword_fields?: boolean;
  group_company_keyword_fields?: boolean;
}

export interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  showToast: (toast: Omit<ToastMessage, 'id' | 'timestamp'>) => void;
  showConfirmationDialog: (dialog: Omit<ConfirmationDialog, 'isOpen'>) => void;
  hideConfirmationDialog: () => void;
  setSearchQuery: (query: string) => void;
  setPage: (page: number) => void;
  setSorting: (sorting: SortingState) => void;
  updateFilter: (field: string, value: FilterValue) => void;
  clearAllFilters: () => void;
  clearFiltersByCategory: (category: string) => void;
  setColumnVisibility: (columns: string[]) => void;
  setView: (view: 'main' | 'terms' | 'privacy' | 'activity' | 'admin' | 'billing' | 'enrichment') => void;
  setDataSource: (dataSource: DataSourceType) => void;
  setNavigation: (navigation: NavigationState) => void;
  selectItem: (id: string) => void;
  deselectItem: (id: string) => void;
  selectAll: (select: boolean) => void;
  clearSelection: () => void;
  saveSelectedContacts: (owner_id: string) => Promise<void>;
  unlockSelectedContacts: (owner_id: string, list_ids: string[], selectAll: boolean, limit?: number) => Promise<void>;
  unlockSelectedAccounts: (list_ids: string[], selectAll: boolean, limit?: number) => Promise<void>;
  addSelectedContactsToList: (owner_id: string, list_ids: string[], selectAll: boolean, limit?: number) => Promise<void>;
  addSelectedAccountsToList: (list_id: string) => Promise<void>;
  removeFromListJobAsync: (list_id: string) => Promise<void>;
  deleteContactsJobAsync: (owner_id: string) => Promise<void>;
  removeFromAccountListJobAsync: (list_id: string) => Promise<void>;
  deleteAccountsJobAsync: () => Promise<void>;
  deleteList: (owner_id: string, list_id: string, list_name: string, onSuccess?: () => void) => Promise<void>;
  deleteAccountList: (owner_id: string, list_id: string, list_name: string, onSuccess?: () => void) => Promise<void>;
  exportSelectedContacts: (owner_id: string, format?: 'json' | 'csv', exportFields?: string[]) => Promise<void>;
  addJobId: (jobId: string, jobType?: Job['job_type']) => void;
  cancelJob: (jobId: string) => Promise<void>;
  removeJob: (jobId: string) => void;
}

export type AppAction = 
  | { type: 'SET_VIEW'; payload: 'main' | 'terms' | 'privacy' | 'activity' | 'admin' | 'billing' | 'enrichment' }
  | { type: 'SET_INITIALIZING'; payload: boolean }
  | { type: 'SET_INITIALIZED'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_API_CONNECTED'; payload: boolean }
  | { type: 'SET_SCHEMA'; payload: SchemaResponse; dataSource: DataSourceType }
  | { type: 'SET_SCHEMA_LOADING'; payload: boolean }
  | { type: 'SET_SCHEMA_ERROR'; payload: string | null }
  | { type: 'SET_METADATA'; payload: MetadataResponse }
  | { type: 'SET_METADATA_LOADING'; payload: boolean }
  | { type: 'SET_METADATA_ERROR'; payload: string | null }
  | { type: 'SET_KEYWORD_SUGGESTIONS'; payload: Record<string, string[]> }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_SORTING'; payload: SortingState }
  | { type: 'SET_FILTERS'; payload: Record<string, FilterValue> }
  | { type: 'SET_DATA_SOURCE'; payload: DataSourceType }
  | { type: 'CLEAR_SEARCH_RESULTS' }
  | { type: 'SET_DATA_SOURCE_COUNT'; payload: { dataSource: DataSourceType; count: number } }
  | { type: 'SET_SEARCH_RESULTS'; payload: { contacts: Contact[]; total: number; totalPages: number; page: number; searchTime: number | null; dataSource: DataSourceType; searchAccess?: SearchAccess | null; } }
  | { type: 'SET_VISIBLE_COLUMNS'; payload: string[] }
  | { type: 'UPDATE_VISIBLE_COLUMNS_FOR_DATA_SOURCE' }
  | { type: 'CLEAR_ALL_FILTERS' }
  | { type: 'CLEAR_FILTERS_BY_CATEGORY'; payload: { category: string } }
  | { type: 'ADD_TOAST'; payload: ToastMessage }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'SELECT_ITEM'; payload: string }
  | { type: 'DESELECT_ITEM'; payload: string }
  | { type: 'SELECT_ALL'; payload: boolean }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SHOW_CONFIRMATION_DIALOG'; payload: ConfirmationDialog }
  | { type: 'HIDE_CONFIRMATION_DIALOG' }
  | { type: 'SET_ACTIVE_JOBS', payload: Job[] }
  | { type: 'REMOVE_JOB', payload: string }
  | { type: 'SET_NAVIGATION', payload: NavigationState }
  | { type: 'SET_PROCESSING'; payload: { uuids: string[]; operation: 'delete' | null; jobId: string } }
  | { type: 'CLEAR_PROCESSING' }
  | { type: 'SET_BILLING_BALANCE'; payload: BillingBalance }
  | { type: 'SET_BILLING_LOADING'; payload: boolean }
  | { type: 'SET_BILLING_ERROR'; payload: string | null }
  | { type: 'SET_GROUP_CONTACT_KEYWORD_FIELDS'; payload: boolean }
  | { type: 'SET_GROUP_COMPANY_KEYWORD_FIELDS'; payload: boolean }
  | { type: 'RELOAD_SCHEMA_FOR_DATA_SOURCE'; payload: DataSourceType };

export interface RequestCacheEntry { data: unknown; timestamp: number; expiry: number; }
export interface RequestCache { [key: string]: RequestCacheEntry; }
export interface CreateJobResponse {
  job_id: string;
  status: string;
  message: string;
}

// Utility function to safely parse numeric values that might be strings
export const safeParseNumber = (value: string | number | null | undefined, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === 'number') return isNaN(value) ? defaultValue : value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

/**
 * Format price with contextual decimal precision
 * - Prices >= $0.01: 2 decimals (e.g., $0.10)
 * - Prices < $0.01: 3 decimals (e.g., $0.005)
 */
export const formatPrice = (price: string | number | null | undefined): string => {
  const numPrice = safeParseNumber(price, 0);
  return numPrice.toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 8, useGrouping: false,
  });
};

export const formatUsd = (value: string | number | null | undefined): string => {
  return safeParseNumber(value, 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

export const hasAvailableCredit = (value: string | number | null | undefined): boolean => {
  return safeParseNumber(value, 0) > 0;
};

// ENHANCED: Billing Types with flexible string/number support
export interface BillingBalance {
  balance: string | number;  // API returns string, UI needs number
  pending_charge_amount: string | number;
  total_contacts_unlocked: string | number;
  total_spent: string | number;
  last_charge_date: string | null;
  last_charge_amount: string | number | null;
  billing_enabled: boolean;
  has_payment_method: boolean;
  custom_price_per_contact?: string | number | null;
}

export interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  total_pages: number;
  size: number;
}

export interface BillingTransaction {
  timestamp: string;
  amount: number;
  description: string;
  balance_after: number;
  reference_id: string;
  stripe_charge_id: string | null;
  status: 'completed' | 'pending' | 'failed';
  event_type: 'usage' | 'charge' | 'refund' | 'adjustment' | 'payment_recovery' | 'credit';
  metadata?: {
    contacts_count?: number;
    contacts_unlocked?: number;  // Used by backend for accurate contact tracking
    price_per_contact?: number;
    total_cost?: number;
    charge_triggered?: boolean;
    previous_balance?: number;
    billing_enabled?: boolean;
    trigger?: string;
    attempt_number?: number;
    retry_delay?: number;
    [key: string]: string | number | boolean | null | undefined;
  };
}

export interface BillingHistory {
  history: BillingTransaction[];
  total: number;
  page: number;
  size: number;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

export interface SetupIntentResponse {
  client_secret: string;
  setup_intent_id: string;
  publishable_key: string;
}

export interface AddFundsResponse {
  success: boolean;
  new_balance: number;
  charge_id: string;
  message: string;
}

export interface BillingStatus {
  stripe_configured: boolean;
  price_per_contact: string;
  minimum_balance: string;
  auto_charge_threshold: string;
  auto_charge_amount: string;
  user_has_payment_method: boolean;
  user_billing_enabled: boolean;
  supports_setup_intent: boolean;
  supports_stripe_connect: boolean;
}

// Pay-As-You-Go Billing Types
export interface PayAsYouGoStatus {
  success: boolean;
  pay_as_you_go_enabled: boolean;
  has_payment_method: boolean;
  current_debt: string;
  max_debt: string;
  price_per_contact: string;
  standard_batch_size: number;
  standard_batch_cost: string;
  can_use_pay_as_you_go: boolean;
  error: string | null;
}

export interface PayAsYouGoUnlockRequest {
  person_uuids?: string[];
  filters?: Record<string, unknown>;
  limit: number;
}

export interface PayAsYouGoUnlockResponse {
  success: boolean;
  contacts_unlocked: number;
  total_charged: string;
  batches_processed: number;
  job_id: string;
  debt_created?: string;
  error?: string;
}

export interface PayAsYouGoBatchCalculation {
  success: boolean;
  total_contacts: number;
  current_debt: number;
  would_create_debt: boolean;
  debt_created?: string;
  new_total_debt: string;
  total_charged: string;
  batches: Array<{
    batch_num: number;
    contacts: number;
    cost: string;
    debt_rollover: string;
    total_charge: string;
  }>;
  total_cost: string;
  batch_count: number;
}

export interface FilterOption {
  value: string;
  label: string;
  count: number;
}

export interface FilterGroup {
  label: string;
  type: 'picklist' | 'boolean' | 'existence';
  options: FilterOption[];
}

// NEW & UPDATED: Admin Analytics Types
export interface AnalyticsOverallSummary {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  client_errors_4xx: number;
  server_errors_5xx: number;
  unique_users: number;
  avg_response_time_ms: number;
}

export interface AnalyticsTopUser {
  owner_id: string;
  user_name: string;
  request_count: number;
  error_rate: number;
  client_errors_4xx: number;
  server_errors_5xx: number;
}

export interface AnalyticsTopEndpoint {
  endpoint: string;
  request_count: number;
  avg_response_time_ms: number;
}

export interface AnalyticsSeriesData {
  name: string;
  data: number[];
}

export interface UserActivityOverTime {
  interval: string;
  timestamps: string[];
  series: AnalyticsSeriesData[];
}

export interface EndpointActivityOverTime {
  interval: string;
  timestamps: string[];
  series: AnalyticsSeriesData[];
}

export interface AdminAnalyticsSummaryResponse {
  time_window_hours: number;
  generated_at: string;
  overall_summary: AnalyticsOverallSummary;
  user_activity_over_time: UserActivityOverTime;
  endpoint_activity_over_time: EndpointActivityOverTime;
  top_users_by_requests: AnalyticsTopUser[];
  top_endpoints_by_requests: AnalyticsTopEndpoint[];
}
export interface AdminSpendWindow {
  spend: number;
  events: number;
  paying_users: number;
  contacts_recorded: number;
}

export interface AdminSpendUser {
  user_name?: string | null;
  user_id: string;
  spend: number;
  events: number;
}

export interface AdminSpendResponse {
  top_window?: '48h' | '30d';
  windows: Record<string, AdminSpendWindow>;
  top_users: AdminSpendUser[];
  lifetime_spend: number;
  lifetime_events: number;
}
